import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, DocumentTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, InboxIcon } from '@heroicons/react/24/outline';
import { getMySignatureInbox, type SignerInboxItem } from '../../services/signerInboxService';
import { isValidRouteDocumentId } from '../../utils/documentRouteUtils';

interface StatusConfig {
  label: string;
  badgeClass: string;
  icon: React.ReactNode;
}

function statusConfig(status: string): StatusConfig {
  switch (status) {
    case 'Signed':
      return {
        label: 'Assinado',
        badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        icon: <CheckCircleIcon className="h-4 w-4 text-emerald-500" />,
      };
    case 'Rejected':
      return {
        label: 'Recusado',
        badgeClass: 'bg-red-50 text-red-700 border-red-200',
        icon: <XCircleIcon className="h-4 w-4 text-red-500" />,
      };
    default:
      return {
        label: 'Pendente',
        badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
        icon: <ClockIcon className="h-4 w-4 text-amber-500" />,
      };
  }
}

export const VisitorInboxPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<SignerInboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMySignatureInbox();
      setItems(data);
    } catch {
      setError('Não foi possível carregar seus documentos para assinatura.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
          <span className="text-sm">Carregando…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto mt-12 flex max-w-sm flex-col items-center gap-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
          <XCircleIcon className="h-7 w-7 text-red-500" />
        </div>
        <p className="text-sm font-medium text-gray-700">{error}</p>
        <button
          type="button"
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          onClick={() => void load()}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-8">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Minhas assinaturas</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Documentos em que você foi convidado a assinar aparecem nesta lista.
          </p>
        </div>

        <div className="flex w-full flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gradient-to-b from-gray-50 to-white px-6 py-14 text-center shadow-sm sm:px-10">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-blue-50 ring-8 ring-blue-50/50">
            <InboxIcon className="h-10 w-10 text-blue-400" aria-hidden />
          </div>
          <h2 className="mt-6 text-lg font-semibold text-gray-900">Nada para assinar no momento</h2>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-gray-600">
            Quando receber um convite, o documento surgirá aqui automaticamente. Verifique também a caixa
            de entrada do e-mail associado à sua conta — o link do convite pode estar lá.
          </p>
          <p className="mt-6 text-xs text-gray-400">
            Precisa de ajuda? Use o menu <span className="font-medium text-gray-500">Ajuda</span> acima.
          </p>
        </div>
      </div>
    );
  }

  const pending = items.filter((i) => i.signerStatus === 'Pending');
  const others = items.filter((i) => i.signerStatus !== 'Pending');

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Minhas assinaturas</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          {pending.length > 0
            ? `${pending.length} documento${pending.length > 1 ? 's' : ''} aguardando sua assinatura.`
            : 'Nenhum documento pendente.'}
        </p>
      </div>

      {pending.length > 0 && (
        <section>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Pendentes</p>
          <ul className="flex flex-col gap-2">
            {pending.map((row) => (
              <InboxCard key={`${row.documentId}-${row.signerStatus}`} row={row} navigate={navigate} />
            ))}
          </ul>
        </section>
      )}

      {others.length > 0 && (
        <section>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Concluídos</p>
          <ul className="flex flex-col gap-2">
            {others.map((row) => (
              <InboxCard key={`${row.documentId}-${row.signerStatus}`} row={row} navigate={navigate} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

function InboxCard({
  row,
  navigate,
}: {
  row: SignerInboxItem;
  navigate: (path: string) => void;
}) {
  const cfg = statusConfig(row.signerStatus);

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          if (!isValidRouteDocumentId(row.documentId)) {
            return;
          }
          navigate(`/sign/inbox/${row.documentId}`);
        }}
        className="group flex w-full items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-gray-300 hover:shadow-md"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
          <DocumentTextIcon className="h-5 w-5 text-gray-500" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-gray-900">
            {row.documentTitle || row.documentId}
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            Atualizado em {new Date(row.updatedAt).toLocaleString('pt-BR')}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span
            className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cfg.badgeClass}`}
          >
            {cfg.icon}
            {cfg.label}
          </span>
          <ChevronRightIcon className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
        </div>
      </button>
    </li>
  );
}
